/**
 * Auth store — leaf module (Perf 3.1.b Step 3).
 *
 * Extracted from store/index.js. This is the highest-value leaf: public
 * layouts/pages and the auth-CTA surfaces depend on `useAuthStore` but should
 * not have to pull the 12k-line store barrel (socket + chat + every other
 * store) to read auth state.
 *
 * Coupling is resolved through RUNTIME accessors rather than static imports:
 *   - getSocket()  — socket layer (connect/disconnect on login/logout)
 *   - getApi()     — non-auth API slices (favorites / account / mfa / users)
 *                    still living in the lib/api monolith
 *   - getStores()  — sibling stores still living in the store barrel
 * This is required for correctness: the barrel (store/index.js) statically
 * imports THIS file to re-export `useAuthStore`, so auth.js must not statically
 * import the barrel back (circular init). The auth API slice is the one
 * dependency safe to import statically because it is itself a leaf
 * (lib/api/auth.js → lib/api/client.js, no store import at eval time).
 *
 * NOTE (bundling): Metro records `require()`d literals as graph edges, so the
 * measured gzip win lands incrementally as the sibling stores / api slices
 * become their own leaves (Steps 4–7). This step is the structural prerequisite
 * and gives the public surfaces a dedicated import target.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Platform } from 'react-native';
import { authApi } from '@/lib/api/auth';
import { trackExperimentAssignments } from '@/constants/utils/analytics';
import { setUserAnalyticsDimensions } from '@/constants/utils/analyticsEvents';
import { snapshotPreserveKeysThenClear } from '@/lib/webLogoutClearLocalStorage';
import { clearUsageCache } from '@/lib/usageCache';
import { clearAllComposerDrafts } from '@/lib/composerDraft';
import { getStorage } from './storage';
import { clearAllStoresOnLogout } from './orchestration';
import { applyServerEnterToSend } from './ui';
import { asEntityId } from './entityId';
import { newerAuthRotation } from '@/lib/syncAuthRotation';

// Runtime-only accessors — see header note. Deferred so module eval doesn't
// reach into the barrel / api monolith / socket layer (avoids circular init).
const getSocket = () => require('@/lib/socket').default;
const getApi = () => require('@/lib/api');
const getStores = () => require('@/store');

function syncBrowserRotation(get, set) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const update = newerAuthRotation(get(), window.localStorage.getItem('auth-storage'));
    if (update) set(update);
  } catch (_) { /* storage may be disabled */ }
}

function clearWebLocalStorageKeepingAppearancePrefs() {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;
  snapshotPreserveKeysThenClear(window.localStorage);
  clearAllComposerDrafts();
}

const useAuthStore = create(
  persist(
    (set, get) => ({
      // Whether the persist middleware has finished rehydrating from storage.
      // Components should check this before making decisions based on auth state
      // to avoid acting on stale default values (e.g., redirecting away before
      // the stored token has loaded).
      _hasHydrated: false,

      // State variables
      token: null,
      refreshToken: null,
      expiresAt: null, // Timestamp for token expiration
      user: {
        id: null,
        email: null,
        username: null,
        flags: {
          isAdmin: false,
          isPlus: false,
          isPlusPremium: false,
          isEmailVerified: false,
          isEmailVerificationRequired: false,
          smsVerificationRequired: false,
          isPhoneVerified: false,
          heldForReview: false,
          isGhost: false,
        },
        stats: {
          total_active_custom_characters_count: 0,
          total_custom_characters_count: 0,
          total_conversation_count: 0,
          total_active_conversation_count: 0,
          total_sent_message_all_count: 0,
          total_sent_message_all_count_1hr: 0,
          total_sent_message_all_count_3hr: 0,
          total_sent_message_text_count: 0,
          total_sent_message_text_count_1hr: 0,
          total_sent_message_text_count_3hr: 0,
          total_sent_message_text_count_1day: 0,
          total_sent_message_image_count: 0,
          total_sent_message_image_count_1day: 0,
          total_sent_message_video_count: 0,
          total_sent_message_audio_count: 0,
          total_received_message_all_count: 0,
          total_received_message_text_count: 0,
          total_received_message_image_success_count: 0,
          total_received_api_request_count: 0
        },
        createdAt: null,
        updatedAt: null,
        status: null,
        favoriteAgentIds: [], // Changed from favoriteAgents array to favoriteAgentIds
        favoriteModelIds: [], // Changed from favoriteModels array to favoriteModelIds
        favoriteExploreItemIds: [],
        favoriteToolIds: [],
        favoritePromptIds: [],
        favoriteTaskIds: [],
        favoriteTaskTemplateIds: [],
        favoriteDeckTemplateIds: [],
        pinnedConversations: [],
        activePurchase: null, // Add activePurchase field
        defaultModel: {
          text: null,
          image: null,
          // Add defaultModel field
          // You can add any other default model-related fields you want to store
        },
        credits: {
          monthlyAllotment: {
            total: 0,
            used: 0,
            remaining: 0,
            resetDate: null
          },
          purchased: { 
            available: 0,
            paidAvailable: 0,
          },
          totalAvailable: 0,
        },
        mfa: {
          isEnabled: false,
          method: null,
          hasBackupCodes: false,
          backupCodesRemaining: 0,
        },

        // Valmar payment data (for quick top-up eligibility)
        valmar: {
          hasPaymentMethod: false,  // Boolean - backend has token, we just know if it exists
          maskedCardData: null,     // Display only - safe for UI: { cardType, maskedNumber, expirationDate }
        },

        // Auto top-up settings
        autoTopup: {
          enabled: false,
          thresholdCredits: 250,        // Default: 250 (options: 100, 250, 500, 1000)
          creditPackId: null,           // User chooses which credit pack to auto-purchase
          monthlySpendLimit: 50,        // Default: $50 (any value up to $3000, or null=unlimited)
          currentMonthSpend: 0,         // Tracks spending this month (resets on 1st)
          consentedAt: null,            // Timestamp when user agreed to terms (required)
          lastTopupAt: null,
          lastTopupCredits: null,
          lastTopupAmount: null,        // Dollar amount of last top-up
        },
          
          // Tracking data
          tracking: {
            completedOnboardingAt: null,
            cachedFileCount: 0,
            cachedMemoryCount: 0,
            cachedVoiceSessionCount: 0,
            cachedUniqueModelsUsedCount: 0,
            cachedImportedConversationCount: 0,
          },

        // Usage data
        usage: {
          inferenceCredits: {
            rows: [],
            isLoading: false,
            error: null,
            pagination: {
              page: 1,
              pages: 1,
              total: 0,
              perPage: 20,
              hasMore: false
            }
          }
        },

        hasPassword: false,
        authProvider: 'local',
        connectedProviders: [],

        fullName: null,
        professionalRole: null,
        avatarPath: null,
        avatarSizeBytes: null,
      },
      isLoadingUser: false,
      hasLoadedServerUser: false,
      userError: null,
      socket: null,
      
      // Card expiration warning state (set by socket on connect for Plus users)
      cardExpirationWarning: null, // { daysRemaining, isExpired, expirationDate, shownToday }

      // Impersonation state. When an admin impersonates a user, the admin's
      // own session is stashed here so "Stop impersonation" can restore it
      // without a re-login. `impersonatorReturnUrl` is the admin dashboard URL
      // to fall back to (when there was no SPA session to restore).
      // `isImpersonating` is the authoritative signal for showing the banner —
      // it's set even when there's no admin SPA session to stash (fresh-tab
      // handoff), so the banner + Stop button always render.
      impersonatorSession: null, // { token, refreshToken, expiresAt, user } | null
      impersonatorReturnUrl: null,
      isImpersonating: false,

      // Actions for tokens
      setAuthData: (accessToken, expiresAt, refreshToken, userData) => {
        // Disconnect any existing socket before switching sessions
        getSocket().disconnectSocket();

        // If switching to a DIFFERENT user (not a token refresh for the same
        // user), clear all data stores so the previous user's collections,
        // chats, agents, etc. don't leak into the new session. This is the
        // root cause of "collections that aren't mine showing in the sidebar"
        // — addCollections merges instead of replacing, so without this clear,
        // a previous user's collections persist across account switches.
        const prevUserId = get().user?.id;
        const newUserId = userData?.id || userData?._id;
        if (String(prevUserId || '') !== String(newUserId || '')) {
          const stores = getStores();
          stores.useCoworkerStore.getState().setComposerMode('chat');
          if (prevUserId && newUserId) {
            stores.useCollectionStore.getState().clearCollections();
            stores.useChatStore.getState().clearChats();
            stores.useAgentStore.getState().clearAgents();
            stores.useModelStore.getState().clearModels();
          }
        }

        const normalizedUserData = get().transformUserData(userData);
        const pinnedChats = Array.isArray(userData?.pinnedConversations)
          ? userData.pinnedConversations.reduce((chats, chat) => {
            if (chat && typeof chat === 'object' && (chat.id || chat._id)) {
              chats.push({ ...chat, isPinned: true });
            }
            return chats;
          }, [])
          : [];

        // Atomically set all auth state in a single update to avoid
        // intermediate states where token is null (which would trigger
        // redirect-to-login in the app layout).
        set({ 
          token: accessToken, 
          expiresAt: expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          refreshToken,
          // Login responses contain pinned conversation summaries, while pin
          // consumers expect ids. Normalize before the sidebar can render.
          user: normalizedUserData,
          isLoadingUser: false,
          hasLoadedServerUser: true,
          userError: null,
          cardExpirationWarning: null,
        });

        // The login response already has enough data to render pinned rows.
        // Do not depend on the follow-up bootstrap request, which can fail.
        if (pinnedChats.length > 0) {
          getStores().useChatStore.getState().addChats(pinnedChats);
        }

        applyServerEnterToSend(normalizedUserData?.preferences?.chat?.enterToSend);

        if (userData?.experiments) {
          trackExperimentAssignments(userData.experiments);
        }
        setUserAnalyticsDimensions(normalizedUserData);

        // Pass userId so connect() doesn't bail on the missing-arg guard.
        // Without this, the socket stays disconnected until _layout.*.jsx's
        // useEffect fires later, and any send in that window surfaces as a
        // spurious "check your internet connection" toast right after login.
        getSocket().connect(accessToken, userData?.id);

        get().fetchUser().catch(() => {});
      },

      clearTokens: () => {
        getSocket().disconnectSocket(); // Disconnect socket
        set({ token: null, refreshToken: null, expiresAt: null });
      },

      // Begin impersonating a target user. Stashes the current (admin) session
      // so stopImpersonation() can restore it, then swaps to the target's
      // tokens via the normal setAuthData path (which reconnects the socket
      // and refetches the user). Idempotent: if already impersonating, the
      // existing stash is preserved (we don't clobber the real admin session).
      startImpersonation: (targetTokens, targetUser, { returnUrl } = {}) => {
        const state = get();

        // Only stash if we actually have an admin session and aren't already
        // mid-impersonation. An admin who opens the handoff link in a fresh
        // tab (no SPA session) has nothing to stash — stop will fall back to
        // returnUrl instead.
        if (!state.impersonatorSession && state.token) {
          set({
            impersonatorSession: {
              token: state.token,
              refreshToken: state.refreshToken,
              expiresAt: state.expiresAt,
              user: state.user,
            },
          });
        }

        if (returnUrl) {
          set({ impersonatorReturnUrl: returnUrl });
        }

        set({ isImpersonating: true });

        // Delegate to setAuthData so the socket reconnect + fetchUser flow is
        // identical to a normal login.
        get().setAuthData(
          targetTokens.access,
          targetTokens.expiresAt,
          targetTokens.refresh,
          targetUser
        );
      },

      // End impersonation and return to the admin session. Returns the
      // returnUrl so the caller can redirect when there was no SPA session to
      // restore. The backend stop call is best-effort and handled by the caller.
      stopImpersonation: () => {
        const { impersonatorSession, impersonatorReturnUrl } = get();

        if (impersonatorSession) {
          // Restore the stashed admin session. setAuthData reconnects the
          // socket and refetches the admin user.
          get().setAuthData(
            impersonatorSession.token,
            impersonatorSession.expiresAt,
            impersonatorSession.refreshToken,
            impersonatorSession.user
          );
          set({ impersonatorSession: null, isImpersonating: false });
          return { restored: true, returnUrl: impersonatorReturnUrl };
        }

        // No admin session to restore (fresh-tab impersonation). Clear and let
        // the caller bounce to the admin dashboard via returnUrl.
        get().clearTokens();
        set({ impersonatorSession: null, impersonatorReturnUrl: null, isImpersonating: false });
        return { restored: false, returnUrl: impersonatorReturnUrl };
      },

      isAuthenticated: () => {
        const state = get();
        return state.token && !state.isTokenExpired();
      },

      isTokenExpired: () => {
        const state = get();
        if (!state.expiresAt) return true;
        
        // Return true if token expires in less than 30 seconds
        const expiresAt = new Date(state.expiresAt).getTime();
        const now = Date.now();
        return !Number.isFinite(expiresAt) || (expiresAt - now) < 30000; // 30 seconds
      },

      // Shared promise to deduplicate concurrent token refresh calls
      _refreshPromise: null,
      
      // reconnectSocket (default true): refresh AND reconnect the socket with
      // the new token — the behavior every existing caller wants. The socket.io
      // auth function in lib/socket.js (which fires before every (re)connect,
      // including auto-reconnects after a blue/green deploy) passes false,
      // because the socket is already (re)connecting there and a second
      // socketManager.connect would race / churn sockets.
      getValidToken: async ({ reconnectSocket = true, forceRefresh = false, rejectedToken } = {}) => {
        syncBrowserRotation(get, set);
        const state = get();
        
        // If no token or refresh token, return null
        if (!state.token || !state.refreshToken) {
          return null;
        }

        // If token is not expired, return current token
        if ((!forceRefresh || (rejectedToken && state.token !== rejectedToken)) && !state.isTokenExpired()) {
          return state.token;
        }

        // Deduplicate: if a refresh is already in-flight, reuse it
        if (state._refreshPromise && state._refreshPromiseToken === state.refreshToken) {
          return state._refreshPromise;
        }

        const sameSession = () => get().refreshToken === state.refreshToken && get().token === state.token;

        const refreshPromise = Promise.resolve().then(async () => {
          try {
            // Attempt to refresh the token
            const { accessToken, refreshToken, expiresAt } = await authApi.refreshToken(state.refreshToken);
            // Logout, account switching or another auth update can happen
            // while the request is in flight. Never resurrect/overwrite it.
            if (!sameSession()) return null;
            
            // Update tokens in store
            set({ 
              token: accessToken,
              refreshToken: refreshToken,
              expiresAt: expiresAt
            });

            if (reconnectSocket) {
              // Check if any conversation is actively streaming before reconnecting socket.
              // Disconnecting mid-stream causes lost stream_end events and frozen outputs.
              const chats = getStores().useChatStore?.getState?.()?.chats || [];
              const isAnyStreaming = chats.some(chat => chat.isInferencing || chat.isInferencingWaiting);

              if (isAnyStreaming) {
                // Defer socket reconnect until after streaming completes to avoid
                // killing the active stream. Update the stored token so the next
                // reconnect (natural or explicit) uses the fresh token.
                getSocket().lastToken = accessToken;
                // scheduleDeferredReconnect waits for conversation_stream_end,
                // with a 5-minute fallback that forces reconnect if the event
                // is missed (network blip, service worker interception, etc.).
                getSocket().scheduleDeferredReconnect(
                  accessToken,
                  get().user?.id
                );
                console.log('[Auth] Token refreshed but deferring socket reconnect — active stream in progress');
              } else {
                // Safe to reconnect socket with new token
                const userId = get().user?.id;
                getSocket().connect(accessToken, userId);
              }
            }

            return accessToken;
          } catch (error) {
            syncBrowserRotation(get, set);
            // Only clear the session if the refresh token itself was
            // explicitly rejected by the server with a typed 401. Transient failures
            // — network errors (status 0), timeouts, or 5xx server errors
            // during a backend deploy or load spike — must NOT log the user
            // out: doing so turned every transient socket `connect_error`
            // into a forced logout, which cascaded into mass logouts across
            // the user base any time the auth endpoint briefly blipped.
            const status = error?.status;
            // Older servers also used untyped 401 for database failures. Keep
            // those sessions during a rolling deployment of the new backend.
            const rejectionCodes = ['TOKEN_EXPIRED', 'TOKEN_NOT_ACTIVE', 'TOKEN_INVALID_SIGNATURE', 'TOKEN_INVALID', 'REFRESH_REJECTED', 'ACCOUNT_INACTIVE'];
            const shouldClearSession = status === 401 && error?.data?.retryable !== true && rejectionCodes.includes(error?.data?.code);
            if (shouldClearSession && sameSession()) {
              state.clearSession();
            } else {
              console.warn(
                '[Auth] Token refresh failed transiently — session preserved:',
                { status: error?.status || 0 }
              );
            }
            throw error;
          } finally {
            if (get()._refreshPromise === refreshPromise) set({ _refreshPromise: null, _refreshPromiseToken: null });
          }
        });

        set({ _refreshPromise: refreshPromise, _refreshPromiseToken: state.refreshToken });
        return refreshPromise;
      },

      // Actions for user data
      setUser: (userData) => {
        set({ user: userData });
      },

      // Helper function to transform purchase data
      transformPurchaseData: (purchase) => {
        if (!purchase) return null;
        
        return {
          id: purchase.id,  
          createdAt: purchase.createdAt,
          price: purchase.price,
          currency: purchase.currency,
          product: purchase.product,
          status: purchase.status,
          transactionId: purchase.transactionId,
          paymentType: purchase.paymentType,
          processor: purchase.processor, 
          processorCustomerId: purchase.processorCustomerId,
          processorSubscriptionId: purchase.processorSubscriptionId,
          recurring: {
            enabled: purchase.recurring?.enabled,
            startDate: purchase.recurring?.startDate,
            endDate: purchase.recurring?.endDate,
            nextBilledAt: purchase.recurring?.nextBilledAt,
            schedule: purchase.recurring?.schedule,
            subscriptionId: purchase.recurring?.subscriptionId,
          },
          paymentReceivedDate: purchase.paymentReceivedDate,
          canceledDate: purchase.canceledDate,
          userId: purchase.userId,
          maskedCardData: {
            cardType: purchase.maskedCardData?.cardType,
            maskedCardNumber: purchase.maskedCardData?.maskedCardNumber,
            expirationDate: purchase.maskedCardData?.expirationDate
          },
          billingIssue: purchase.billingIssue ? {
            active: purchase.billingIssue.active || false,
            gracePeriodExpiresAt: purchase.billingIssue.gracePeriodExpiresAt || null,
            retryPeriodExpiresAt: purchase.billingIssue.retryPeriodExpiresAt || null,
            detectedAt: purchase.billingIssue.detectedAt || null,
          } : null,
        };
      },

      // Helper function to transform user data
      transformUserData: (responseUser) => {
        if (!responseUser) return null;

        // Normalize id lists from either API populate-objects or already-
        // transformed store shape (setAuthData / stopImpersonation). Prefer
        // populated arrays when present so a second pass is idempotent.
        const normalizeIdList = (value) => {
          if (!Array.isArray(value)) return [];
          return value.reduce((ids, item) => {
            const id = item?.id ?? item?._id ?? item;
            if (id != null && id !== '') ids.push(String(id));
            return ids;
          }, []);
        };
        const favoriteAgentIds = Array.isArray(responseUser.favoriteAgents)
          ? normalizeIdList(responseUser.favoriteAgents)
          : normalizeIdList(responseUser.favoriteAgentIds);
        const favoriteModelIds = Array.isArray(responseUser.favoriteModels)
          ? normalizeIdList(responseUser.favoriteModels)
          : normalizeIdList(responseUser.favoriteModelIds);
        const favoriteToolIds = Array.isArray(responseUser.favoriteTools)
          ? normalizeIdList(responseUser.favoriteTools)
          : normalizeIdList(responseUser.favoriteToolIds);
        const favoritePromptIds = Array.isArray(responseUser.favoritePrompts)
          ? normalizeIdList(responseUser.favoritePrompts)
          : normalizeIdList(responseUser.favoritePromptIds);
        const favoriteTaskIds = normalizeIdList(responseUser.favoriteTaskIds);
        const favoriteTaskTemplateIds = normalizeIdList(responseUser.favoriteTaskTemplateIds);
        const favoriteDeckTemplateIds = normalizeIdList(responseUser.favoriteDeckTemplateIds);
        const pinnedConversationIds = normalizeIdList(responseUser.pinnedConversations);

        // Most user refresh endpoints do not include activePurchase. Preserve
        // the independently fetched billing record when the field is omitted;
        // only an explicit null should clear it.
        const transformedPurchase = Object.prototype.hasOwnProperty.call(responseUser, 'activePurchase')
          ? get().transformPurchaseData(responseUser.activePurchase)
          : (get().user?.activePurchase || null);

        // Server-resolved geo (Cloudflare header / IP lookup) drives checkout
        // routing. Only /json/user/me[/slim] send it, so preserve the last known
        // values instead of letting a partial refresh blank them out.
        const geoFields = ['isInternational', 'countryCode'].reduce((acc, key) => {
          acc[key] = Object.prototype.hasOwnProperty.call(responseUser, key)
            ? responseUser[key]
            : get().user?.[key];
          return acc;
        }, {});

        // Return explicitly mapped user data structure
        return {
          // Core user fields
          id: responseUser.id || null,
          email: responseUser.email || null,
          username: responseUser.username || null,
          // Passed through uncoerced: an explicit null (country unresolvable)
          // has to stay distinguishable from an absent field.
          isInternational: geoFields.isInternational,
          countryCode: geoFields.countryCode,
          
          // Flags object
          flags: {
            isAdmin: responseUser.flags?.isAdmin || false,
            isPlus: responseUser.flags?.isPlus || false,
            isPlusPremium: responseUser.flags?.isPlusPremium || false,
            isComped: responseUser.flags?.isComped || false,
            isEmailVerified: responseUser.flags?.isEmailVerified || false,
            isEmailVerificationRequired: responseUser.flags?.isEmailVerificationRequired || false,
            smsVerificationRequired: responseUser.flags?.smsVerificationRequired || false,
            isPhoneVerified: responseUser.flags?.isPhoneVerified || false,
            heldForReview: responseUser.flags?.heldForReview === true,
            isGhost: responseUser.flags?.isGhost || false,
          },
          reviewHold: {
            heldAt: responseUser?.reviewHold?.heldAt || null,
          },
          
          // Stats object
          stats: {
            total_active_custom_characters_count: responseUser.stats?.total_active_custom_characters_count || 0,
            total_custom_characters_count: responseUser.stats?.total_custom_characters_count || 0,
            total_conversation_count: responseUser.stats?.total_conversation_count || 0,
            total_active_conversation_count: responseUser.stats?.total_active_conversation_count || 0,
            total_sent_message_all_count: responseUser.stats?.total_sent_message_all_count || 0,
            total_sent_message_all_count_1hr: responseUser.stats?.total_sent_message_all_count_1hr || 0,
            total_sent_message_all_count_3hr: responseUser.stats?.total_sent_message_all_count_3hr || 0,
            total_sent_message_text_count: responseUser.stats?.total_sent_message_text_count || 0,
            total_sent_message_text_count_1hr: responseUser.stats?.total_sent_message_text_count_1hr || 0,
            total_sent_message_text_count_3hr: responseUser.stats?.total_sent_message_text_count_3hr || 0,
            total_sent_message_text_count_1day: responseUser.stats?.total_sent_message_text_count_1day || 0,
            total_sent_message_image_count: responseUser.stats?.total_sent_message_image_count || 0,
            total_sent_message_image_count_1day: responseUser.stats?.total_sent_message_image_count_1day || 0,
            total_sent_message_video_count: responseUser.stats?.total_sent_message_video_count || 0,
            total_sent_message_audio_count: responseUser.stats?.total_sent_message_audio_count || 0,
            total_received_message_all_count: responseUser.stats?.total_received_message_all_count || 0,
            total_received_message_text_count: responseUser.stats?.total_received_message_text_count || 0,
            total_received_message_image_success_count: responseUser.stats?.total_received_message_image_success_count || 0,
            total_received_api_request_count: responseUser.stats?.total_received_api_request_count || 0
          },

          // Timestamps
          createdAt: responseUser.createdAt || null,
          updatedAt: responseUser.updatedAt || null,
          
          // Status
          status: responseUser.status || null,
          
          // Transformed arrays
          favoriteAgentIds: favoriteAgentIds,
          favoriteModelIds: favoriteModelIds,
          favoriteExploreItemIds: Array.isArray(responseUser.favoriteExploreItemIds) ? responseUser.favoriteExploreItemIds : [],
          favoriteToolIds,
          favoritePromptIds,
          favoriteTaskIds,
          favoriteTaskTemplateIds,
          favoriteDeckTemplateIds,
          pinnedConversations: pinnedConversationIds,

          // Full responses populate the bot; preserve the normalized ID through
          // partial user refreshes and a second transform of persisted state.
          defaultAgentId: Object.prototype.hasOwnProperty.call(responseUser, 'defaultAgent')
            ? (responseUser.defaultAgent?.id || responseUser.defaultAgent?._id || responseUser.defaultAgent || null)
            : Object.prototype.hasOwnProperty.call(responseUser, 'defaultAgentId')
              ? responseUser.defaultAgentId
              : (responseUser.id === get().user?.id ? get().user?.defaultAgentId || null : null),
          
          defaultModel: {
            text: responseUser?.defaultModel?.text?.id || responseUser?.defaultModel?.text || null,
            image: responseUser?.defaultModel?.image?.id || responseUser?.defaultModel?.image || null,
          },

          // Subscription expiration (may be set on user object by backend)
          plusExpiresAt: responseUser.plusExpiresAt || null,

          // Active purchase
          activePurchase: transformedPurchase,

          credits: {
            monthlyAllotment: {
              total: responseUser?.credits?.monthlyAllotment?.total || 0,
              used: responseUser?.credits?.monthlyAllotment?.used || 0,
              remaining: responseUser?.credits?.monthlyAllotment?.remaining || 0,
              resetDate: responseUser?.credits?.monthlyAllotment?.resetDate || null
            },
            purchased: {
              available: responseUser?.credits?.purchased?.available || 0,
              paidAvailable: responseUser?.credits?.purchased?.paidAvailable || 0,
            },
            totalAvailable: responseUser?.credits?.totalAvailable || 0,
            // Preserve credits-endpoint value when a partial user payload omits it.
            pendingVerificationCredits: responseUser?.credits?.pendingVerificationCredits
              ?? get().user?.credits?.pendingVerificationCredits
              ?? 0,
          },
          
          fullName: responseUser.fullName || null,
          professionalRole: responseUser.professionalRole || null,
          avatarPath: responseUser.avatarPath || null,
          avatarSizeBytes: responseUser.avatarSizeBytes || null,

          mfa: {
            isEnabled: responseUser?.mfa?.isEnabled || false,
            method: responseUser?.mfa?.method || null,
            hasBackupCodes: Array.isArray(responseUser?.mfa?.backupCodes) && responseUser?.mfa?.backupCodes?.length > 0,
            backupCodesRemaining: Array.isArray(responseUser?.mfa?.backupCodes) ? responseUser?.mfa?.backupCodes?.length : 0,
          },

          // Valmar payment data (for quick top-up eligibility)
          valmar: {
            hasPaymentMethod: responseUser?.valmar?.hasPaymentMethod || false,
            maskedCardData: responseUser?.valmar?.maskedCardData ? {
              cardType: responseUser.valmar.maskedCardData.cardType || null,
              maskedNumber: responseUser.valmar.maskedCardData.maskedNumber || null,
              expirationDate: responseUser.valmar.maskedCardData.expirationDate || null,
            } : null,
          },

          // Whop subscription flag from /json/user/me[/slim]. Partial
          // refreshes omit it, so keep the last known value.
          whop: Object.prototype.hasOwnProperty.call(responseUser, 'whop')
            ? {
                hasSubscription: Boolean(responseUser.whop?.hasSubscription),
                status: responseUser.whop?.status || null,
              }
            : (get().user?.whop || { hasSubscription: false, status: null }),

          // Auto top-up settings
          autoTopup: {
            enabled: responseUser?.autoTopup?.enabled || false,
            thresholdCredits: responseUser?.autoTopup?.thresholdCredits || 250,
            creditPackId: responseUser?.autoTopup?.creditPackId || null,
            monthlySpendLimit: responseUser?.autoTopup?.monthlySpendLimit ?? 50,
            currentMonthSpend: responseUser?.autoTopup?.currentMonthSpend || 0,
            consentedAt: responseUser?.autoTopup?.consentedAt || null,
            lastTopupAt: responseUser?.autoTopup?.lastTopupAt || null,
            lastTopupCredits: responseUser?.autoTopup?.lastTopupCredits || null,
            lastTopupAmount: responseUser?.autoTopup?.lastTopupAmount || null,
          },
          
          tracking: {
            completedOnboardingAt: responseUser?.tracking?.completedOnboardingAt || null,
            cachedFileCount: responseUser?.tracking?.cachedFileCount || 0,
            cachedMemoryCount: responseUser?.tracking?.cachedMemoryCount || 0,
            cachedVoiceSessionCount: responseUser?.tracking?.cachedVoiceSessionCount || 0,
            cachedUniqueModelsUsedCount: responseUser?.tracking?.cachedUniqueModelsUsedCount || 0,
            cachedImportedConversationCount: responseUser?.tracking?.cachedImportedConversationCount || 0,
          },

          experiments: responseUser?.experiments || {},

          hasPassword: responseUser?.hasPassword || false,
          authProvider: responseUser?.authProvider || 'local',
          connectedProviders: responseUser?.connectedProviders || [],

          preferences: {
            memory: (() => {
              const incoming = responseUser?.preferences?.memory;
              const existing = get().user?.preferences?.memory || {};
              const hasIncoming = incoming && typeof incoming === 'object';
              const incomingSummary = hasIncoming && Object.prototype.hasOwnProperty.call(incoming, 'profileSummary')
                ? incoming.profileSummary
                : undefined;
              const incomingEdited = hasIncoming && Object.prototype.hasOwnProperty.call(incoming, 'profileSummaryUserEdited')
                ? incoming.profileSummaryUserEdited === true
                : undefined;
              return {
                isEnabled: hasIncoming ? incoming.isEnabled === true : existing.isEnabled === true,
                isAutoGenerationEnabled: hasIncoming
                  ? incoming.isAutoGenerationEnabled === true
                  : existing.isAutoGenerationEnabled === true,
                autoSaveEnabled: hasIncoming
                  ? incoming.autoSaveEnabled !== false
                  : existing.autoSaveEnabled !== false,
                dreamingEnabled: hasIncoming
                  ? incoming.dreamingEnabled !== false
                  : existing.dreamingEnabled !== false,
                profileSummary: incomingSummary !== undefined
                  ? (incomingSummary || null)
                  : (existing.profileSummary || null),
                profileSummaryUserEdited: incomingEdited !== undefined
                  ? incomingEdited
                  : existing.profileSummaryUserEdited === true,
              };
            })(),
            chat: {
              followUpSuggestionsEnabled: responseUser?.preferences?.chat?.followUpSuggestionsEnabled !== false,
              collectionSuggestionsEnabled: responseUser?.preferences?.chat?.collectionSuggestionsEnabled !== false,
              stockChartsEnabled: responseUser?.preferences?.chat?.stockChartsEnabled !== false,
              enterToSend: responseUser?.preferences?.chat?.enterToSend !== false,
              workModeSuggestionsEnabled: responseUser?.preferences?.chat?.workModeSuggestionsEnabled !== false,
            },
            appearance: {
              selectedPet: responseUser?.preferences?.appearance?.selectedPet || null,
              petSelectionCompleted: responseUser?.preferences?.appearance?.petSelectionCompleted === true,
              petPromoDismissed: responseUser?.preferences?.appearance?.petPromoDismissed === true,
            },
          },
        };
      },

      updateUserEmail: (newEmail) => {
        set(state => ({
          user: {
            ...state.user,
            email: newEmail,
            flags: {
              ...state.user.flags,
              isEmailVerified: false
            }
          }
        }));
      },

      updateUserPreferences: (preferences) => {
        set(state => ({
          user: {
            ...state.user,
            preferences: {
              ...state.user.preferences,
              memory: {
                ...state.user.preferences?.memory,
                ...(preferences.memory || {})
              },
              chat: {
                ...state.user.preferences?.chat,
                ...(preferences.chat || {})
              },
              appearance: {
                ...state.user.preferences?.appearance,
                ...(preferences.appearance || {})
              }
            }
          }
        }));
      },

      // Auto top-up actions
      updateAutoTopupSettings: (settings) => {
        set(state => ({
          user: {
            ...state.user,
            autoTopup: {
              ...state.user.autoTopup,
              ...settings,
            }
          }
        }));
      },

      // Quick purchase - updates credits after successful charge
      updateUserCreditsAfterPurchase: (creditsData) => {
        set(state => ({
          user: {
            ...state.user,
            credits: {
              ...state.user.credits,
              purchased: {
                ...state.user.credits.purchased,
                available: creditsData.purchasedCreditsAvailable ?? state.user.credits.purchased.available,
                // Never fall back paidAvailable → total purchased (signup packs
                // would incorrectly unlock gated models after quick purchase).
                paidAvailable: creditsData.paidCreditsAvailable
                  ?? state.user.credits.purchased.paidAvailable,
              },
              totalAvailable: creditsData.totalAvailable ?? state.user.credits.totalAvailable,
            }
          }
        }));
      },

      // Model favorites

      addFavoriteModelId: (modelId) => {
        const currentUser = get().user;
        const currentFavoriteIds = [...new Set(currentUser.favoriteModelIds || [])];
        
        if (!currentFavoriteIds.includes(modelId)) {
          set({
            user: {
              ...currentUser,
              favoriteModelIds: [...currentFavoriteIds, modelId]
            }
          });
        }
      },

      removeFavoriteModelId: (modelId) => {
        const currentUser = get().user;
        const currentFavoriteIds = [...new Set(currentUser.favoriteModelIds || [])];
        
        set({
          user: {
            ...currentUser,
            favoriteModelIds: currentFavoriteIds.filter(id => id !== modelId)
          }
        });
      },

      addFavoriteAgentId: (agentId) => {
        const currentUser = get().user;
        const currentFavoriteIds = [...new Set(currentUser.favoriteAgentIds || [])];
        
        if (!currentFavoriteIds.includes(agentId)) {
          set({
            user: {
              ...currentUser,
              favoriteAgentIds: [...currentFavoriteIds, agentId]
            }
          });
        }
      },

      toggleFavoriteModel: async (modelId) => {
        try {
          const currentUser = get().user;
          const currentFavoriteIds = [...new Set(currentUser.favoriteModelIds || [])];
          const isFavorited = currentFavoriteIds.includes(modelId);
          
          const success = isFavorited 
            ? await getApi().modelsApi.unfavoriteModel(modelId)
            : await getApi().modelsApi.favoriteModel(modelId);
          
          if (!success) throw 'Failed to toggle favorite';

          const model = getStores().useModelStore.getState().getModelById(modelId);
          if (model) {
            model.isSaved = !isFavorited;
            getStores().useModelStore.getState().addModels(model);
          }
          
          // Use helper functions to update the favoriteAgentIds
          if (isFavorited) {
            get().removeFavoriteModelId(modelId);
          } else {
            get().addFavoriteModelId(modelId);
          }
          
          return success;
        } catch (error) {
          console.error('Error toggling favorite model:', error, error.message, error.data);
          throw error;
        }
      },

      // Agent favorites

      removeFavoriteAgentId: (agentId) => {
        const currentUser = get().user;
        const currentFavoriteIds = [...new Set(currentUser.favoriteAgentIds || [])];
        
        set({
          user: {
            ...currentUser,
            favoriteAgentIds: currentFavoriteIds.filter(id => id !== agentId)
          }
        });
      },

      toggleFavoriteAgent: async (agentId) => {
        try {
          const currentUser = get().user;
          const currentFavoriteIds = [...new Set(currentUser.favoriteAgentIds || [])];
          const isFavorited = currentFavoriteIds.includes(agentId);
          
          const success = isFavorited 
            ? await getApi().agentsApi.unfavoriteAgent(agentId)
            : await getApi().agentsApi.favoriteAgent(agentId);
          
          if (!success) throw 'Failed to toggle favorite';

          const agent = getStores().useAgentStore.getState().getAgentById(agentId);
          if (agent) {
            agent.isSaved = !isFavorited;
            getStores().useAgentStore.getState().addAgents(agent);
          }
          
          // Use helper functions to update the favoriteAgentIds
          if (isFavorited) {
            get().removeFavoriteAgentId(agentId);
          } else {
            get().addFavoriteAgentId(agentId);
          }
          
          return success;
        } catch (error) {
          console.error('Error toggling favorite agent:', error, error.message, error.data);
          throw error;
        }
      },

      // Explore item favorites

      addFavoriteExploreItemId: (itemId) => {
        const currentUser = get().user;
        const currentIds = [...new Set(currentUser.favoriteExploreItemIds || [])];
        if (!currentIds.includes(itemId)) {
          set({ user: { ...currentUser, favoriteExploreItemIds: [...currentIds, itemId] } });
        }
      },

      removeFavoriteExploreItemId: (itemId) => {
        const currentUser = get().user;
        const currentIds = [...new Set(currentUser.favoriteExploreItemIds || [])];
        set({ user: { ...currentUser, favoriteExploreItemIds: currentIds.filter(id => id !== itemId) } });
      },

      // Accepts the full explore item so we can both:
      //  - call the backend with the URL-friendly slug, and
      //  - keep optimistic state keyed by the canonical _id (which is what
      //    the user document's `favoriteExploreItems` array stores).
      toggleFavoriteExploreItem: async (item) => {
        const itemId = item?.id || null;
        const slug = item?.slug || null;
        if (!itemId || !slug) {
          throw new Error('toggleFavoriteExploreItem: item must include id and slug');
        }
        const currentUser = get().user;
        const currentIds = [...new Set(currentUser.favoriteExploreItemIds || [])];
        const isFavorited = currentIds.includes(itemId);

        // Optimistic update first so the heart fills instantly.
        if (isFavorited) get().removeFavoriteExploreItemId(itemId);
        else get().addFavoriteExploreItemId(itemId);

        try {
          const success = isFavorited
            ? await getApi().exploreApi.unfavoriteItem(slug)
            : await getApi().exploreApi.favoriteItem(slug);
          if (!success) throw new Error('Failed to toggle favorite');
          return !isFavorited; // returns the new favorited state
        } catch (error) {
          // Roll back on failure
          if (isFavorited) get().addFavoriteExploreItemId(itemId);
          else get().removeFavoriteExploreItemId(itemId);
          console.error('Error toggling favorite explore item:', error);
          throw error;
        }
      },

      addFavoriteToolId: (toolId) => {
        const currentUser = get().user;
        const id = String(toolId);
        const currentIds = [...new Set((currentUser.favoriteToolIds || []).map(String))];
        if (!currentIds.includes(id)) {
          set({ user: { ...currentUser, favoriteToolIds: [...currentIds, id] } });
        }
      },

      removeFavoriteToolId: (toolId) => {
        const currentUser = get().user;
        const id = String(toolId);
        const currentIds = [...new Set((currentUser.favoriteToolIds || []).map(String))];
        set({
          user: {
            ...currentUser,
            favoriteToolIds: currentIds.filter(x => x !== id),
          },
        });
      },

      toggleFavoriteTool: async (toolId) => {
        const currentUser = get().user;
        const id = String(toolId);
        const currentIds = [...new Set((currentUser.favoriteToolIds || []).map(String))];
        const isFavorited = currentIds.includes(id);

        if (isFavorited) get().removeFavoriteToolId(id);
        else get().addFavoriteToolId(id);

        try {
          const success = isFavorited
            ? await getApi().toolsApi.unfavoriteTool(id)
            : await getApi().toolsApi.favoriteTool(id);
          if (!success) throw new Error('Failed to toggle favorite tool');
          return !isFavorited;
        } catch (error) {
          if (isFavorited) get().addFavoriteToolId(id);
          else get().removeFavoriteToolId(id);
          console.error('Error toggling favorite tool:', error);
          throw error;
        }
      },

      addFavoritePromptId: (promptId) => {
        const currentUser = get().user;
        const id = String(promptId);
        const currentIds = [...new Set((currentUser.favoritePromptIds || []).map(String))];
        if (!currentIds.includes(id)) {
          set({ user: { ...currentUser, favoritePromptIds: [...currentIds, id] } });
        }
      },

      removeFavoritePromptId: (promptId) => {
        const currentUser = get().user;
        const id = String(promptId);
        const currentIds = [...new Set((currentUser.favoritePromptIds || []).map(String))];
        set({
          user: {
            ...currentUser,
            favoritePromptIds: currentIds.filter(x => x !== id),
          },
        });
      },

      toggleFavoritePrompt: async (promptId) => {
        const currentUser = get().user;
        const id = String(promptId);
        const currentIds = [...new Set((currentUser.favoritePromptIds || []).map(String))];
        const isFavorited = currentIds.includes(id);

        if (isFavorited) get().removeFavoritePromptId(id);
        else get().addFavoritePromptId(id);

        try {
          const success = isFavorited
            ? await getApi().promptsApi.unfavoritePrompt(id)
            : await getApi().promptsApi.favoritePrompt(id);
          if (!success) throw new Error('Failed to toggle favorite prompt');
          return !isFavorited;
        } catch (error) {
          if (isFavorited) get().addFavoritePromptId(id);
          else get().removeFavoritePromptId(id);
          console.error('Error toggling favorite prompt:', error);
          throw error;
        }
      },

      addFavoriteTaskId: (taskId) => {
        const currentUser = get().user;
        const id = String(taskId);
        const currentIds = [...new Set((currentUser.favoriteTaskIds || []).map(String))];
        if (!currentIds.includes(id)) {
          set({ user: { ...currentUser, favoriteTaskIds: [...currentIds, id] } });
        }
      },

      removeFavoriteTaskId: (taskId) => {
        const currentUser = get().user;
        const id = String(taskId);
        const currentIds = [...new Set((currentUser.favoriteTaskIds || []).map(String))];
        set({
          user: {
            ...currentUser,
            favoriteTaskIds: currentIds.filter(x => x !== id),
          },
        });
      },

      toggleFavoriteTask: async (taskId) => {
        const currentUser = get().user;
        const id = String(taskId);
        const currentIds = [...new Set((currentUser.favoriteTaskIds || []).map(String))];
        const isFavorited = currentIds.includes(id);

        if (isFavorited) get().removeFavoriteTaskId(id);
        else get().addFavoriteTaskId(id);

        try {
          const success = isFavorited
            ? await getApi().inferenceTasksApi.unfavoriteTask(id)
            : await getApi().inferenceTasksApi.favoriteTask(id);
          if (!success) throw new Error('Failed to toggle favorite task');
          return !isFavorited;
        } catch (error) {
          if (isFavorited) get().addFavoriteTaskId(id);
          else get().removeFavoriteTaskId(id);
          console.error('Error toggling favorite task:', error);
          throw error;
        }
      },

      addFavoriteTaskTemplateId: (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteTaskTemplateIds || []).map(String))];
        if (!currentIds.includes(id)) {
          set({ user: { ...currentUser, favoriteTaskTemplateIds: [...currentIds, id] } });
        }
      },

      removeFavoriteTaskTemplateId: (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteTaskTemplateIds || []).map(String))];
        set({
          user: {
            ...currentUser,
            favoriteTaskTemplateIds: currentIds.filter(x => x !== id),
          },
        });
      },

      toggleFavoriteTaskTemplate: async (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteTaskTemplateIds || []).map(String))];
        const isFavorited = currentIds.includes(id);

        if (isFavorited) get().removeFavoriteTaskTemplateId(id);
        else get().addFavoriteTaskTemplateId(id);

        try {
          const success = isFavorited
            ? await getApi().taskTemplatesApi.unfavoriteTemplate(id)
            : await getApi().taskTemplatesApi.favoriteTemplate(id);
          if (!success) throw new Error('Failed to toggle favorite template');
          return !isFavorited;
        } catch (error) {
          if (isFavorited) get().addFavoriteTaskTemplateId(id);
          else get().removeFavoriteTaskTemplateId(id);
          console.error('Error toggling favorite task template:', error);
          throw error;
        }
      },

      addFavoriteDeckTemplateId: (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteDeckTemplateIds || []).map(String))];
        if (!currentIds.includes(id)) {
          set({ user: { ...currentUser, favoriteDeckTemplateIds: [...currentIds, id] } });
        }
      },

      removeFavoriteDeckTemplateId: (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteDeckTemplateIds || []).map(String))];
        set({
          user: {
            ...currentUser,
            favoriteDeckTemplateIds: currentIds.filter(x => x !== id),
          },
        });
      },

      toggleFavoriteDeckTemplate: async (templateId) => {
        const currentUser = get().user;
        const id = String(templateId);
        const currentIds = [...new Set((currentUser.favoriteDeckTemplateIds || []).map(String))];
        const isFavorited = currentIds.includes(id);

        if (isFavorited) get().removeFavoriteDeckTemplateId(id);
        else get().addFavoriteDeckTemplateId(id);

        try {
          const success = isFavorited
            ? await getApi().deckTemplatesApi.unfavoriteDeckTemplate(id)
            : await getApi().deckTemplatesApi.favoriteDeckTemplate(id);
          if (!success) throw new Error('Failed to toggle favorite deck recipe');
          return !isFavorited;
        } catch (error) {
          if (isFavorited) get().addFavoriteDeckTemplateId(id);
          else get().removeFavoriteDeckTemplateId(id);
          console.error('Error toggling favorite deck template:', error);
          throw error;
        }
      },

      _fetchUserPromise: null,

      fetchUser: async () => {
        // Deduplicate concurrent calls — if a fetch is already in flight,
        // return the existing promise instead of firing a second request.
        const existing = get()._fetchUserPromise;
        if (existing) return existing;

        const promise = (async () => {
        try {
          set({ isLoadingUser: true, userError: null });

          // Fire the lightweight credits endpoint in parallel. The full
          // /json/user/me payload is heavy (collections, conversations,
          // favorites populates, etc.) and the credit balance is buried inside
          // it, so the credit chip would otherwise sit at 0 until the whole
          // payload lands (several seconds). This resolves the small
          // /json/user/credits call first and populates the balance early; the
          // me payload later overwrites `user` with the same fresh value.
          get().fetchUserCredits().catch(() => {});

          const response = await authApi.getCurrentUser();
          
          // Add favorite agents to the agents store
          if (response?.user?.favoriteAgents?.length) {
            getStores().useAgentStore.getState().addAgents(response.user.favoriteAgents);
          }

          // Add favorite models to the models store
          if (response?.user?.favoriteModels?.length) {
            getStores().useModelStore.getState().addModels(response.user.favoriteModels);
          }

          if (response?.user?.favoriteTools?.length) {
            getStores().useToolStore.getState().upsertTools(response.user.favoriteTools);
          }

          if (response?.user?.favoritePrompts?.length) {
            getStores().usePromptStore.getState().upsertPrompts(response.user.favoritePrompts);
          }

          if (!!response?.user?.defaultAgent && response?.user?.defaultAgent?.id) {
            getStores().useAgentStore.getState().addAgents([response.user.defaultAgent]);
          }

          // Add default models to the models store so settings UI can display their names
          const defaultModels = [response?.user?.defaultModel?.text, response?.user?.defaultModel?.image].filter(m => m?.id);
          if (defaultModels.length > 0) {
            getStores().useModelStore.getState().addModels(defaultModels);
          }

          // Transform and set the user data FIRST to ensure pinnedConversations are available
          const userDataWithIds = get().transformUserData(response.user);
          set({ user: userDataWithIds, apiKey: response.apiKey, hasLoadedServerUser: true });
          setUserAnalyticsDimensions(userDataWithIds);
          applyServerEnterToSend(userDataWithIds?.preferences?.chat?.enterToSend);

          // Add collections to collections store
          if (response?.collections?.data?.length) {
            getStores().useCollectionStore.getState().addCollections(response.collections.data);
            // Update collection pagination if needed
            getStores().useCollectionStore.getState().setPagination({
              current: response.collections.pagination.page,
              pages: Math.ceil(response.collections.pagination.total / response.collections.pagination.perPage),
              total: response.collections.pagination.total,
              perPage: response.collections.pagination.perPage,
              hasMore: response.collections.pagination.hasMore
            });
          }

          // NOW add conversations to chat store after user data is set
          if (response?.conversations?.data?.length) {
            getStores().useChatStore.getState().addChats(response.conversations.data);
            // Update chat pagination if needed
            getStores().useChatStore.getState().setPagination({
              current: response.conversations.pagination.page,
              pages: Math.ceil(response.conversations.pagination.total / response.conversations.pagination.perPage),
              total: response.conversations.pagination.total,
              perPage: response.conversations.pagination.perPage,
              hasMore: response.conversations.pagination.hasMore
            });
          }
          // PLUS_WINBACK_PRESSURE §1: the bootstrap endpoint also
          // emits the `plusLock` payload for churned users so the
          // sidebar locked-card renders on first paint, not later
          // when /json/conversations finally fires. Pass it through
          // to the chat store. For non-churned users this is a no-op.
          if (response?.conversations?.plusLock) {
            getStores().useChatStore.setState({ plusLock: response.conversations.plusLock });
          }

          // Add pinned conversations to chat store and mark them as pinned
          if (response?.user?.pinnedConversations?.length) {
            const pinnedChats = response.user.pinnedConversations.map(chat => ({
              ...chat,
              isPinned: true // Mark as pinned
            }));
            getStores().useChatStore.getState().addChats(pinnedChats);
          }

          // Bootstrap `addChats` merges into the persisted list and will not
          // drop chats deleted on another device. Reconcile in the background
          // so the native sidebar catches up without waiting on the socket.
          try {
            getStores().useChatStore.getState().reconcileDeletedChats?.();
          } catch (_) { /* best-effort */ }
          
          set({ isLoadingUser: false });
          return userDataWithIds;
        } catch (error) {
          console.error('Error fetching user:', error);
          set({ isLoadingUser: false, userError: error.message || 'Failed to fetch user' });
          throw error;
        } finally {
          set({ _fetchUserPromise: null });
        }
        })();

        set({ _fetchUserPromise: promise });
        return promise;
      },

      // Lightweight user fetch — only returns user object, no collections/conversations/apiKey.
      // Use for token refresh recovery and background re-auth where we don't need the full payload.
      fetchUserSlim: async () => {
        try {
          const response = await authApi.getCurrentUserSlim();

          if (response?.user) {
            // Add favorite agents/models to their stores if present
            if (response.user.favoriteAgents?.length) {
              getStores().useAgentStore.getState().addAgents(response.user.favoriteAgents);
            }
            if (response.user.favoriteModels?.length) {
              getStores().useModelStore.getState().addModels(response.user.favoriteModels);
            }
            if (response.user.defaultAgent?.id) {
              getStores().useAgentStore.getState().addAgents([response.user.defaultAgent]);
            }

            const userDataWithIds = get().transformUserData(response.user);
            set({ user: userDataWithIds, hasLoadedServerUser: true });
            setUserAnalyticsDimensions(userDataWithIds);
            applyServerEnterToSend(userDataWithIds?.preferences?.chat?.enterToSend);
            return userDataWithIds;
          }
        } catch (error) {
          console.error('Error fetching slim user:', error);
          // Non-critical — fall through silently
        }
      },

      clearUser: () => {
        set({
          token: null,
          refreshToken: null,
          expiresAt: null,
          socket: null,
          hasLoadedServerUser: false,
          user: {
            id: null,
            email: null,
            username: null,
            flags: {
              isAdmin: false,
              isPlus: false,
          isPlusPremium: false,
              isEmailVerified: false,
              isEmailVerificationRequired: false,
              smsVerificationRequired: false,
              isPhoneVerified: false,
              heldForReview: false,
              isGhost: false,
            },
            stats: {
              total_active_custom_characters_count: 0,
              total_custom_characters_count: 0,
              total_conversation_count: 0,
              total_active_conversation_count: 0,
              total_sent_message_all_count: 0,
              total_sent_message_all_count_1hr: 0,
              total_sent_message_all_count_3hr: 0,
              total_sent_message_text_count: 0,
              total_sent_message_text_count_1hr: 0,
              total_sent_message_text_count_3hr: 0,
              total_sent_message_text_count_1day: 0,
              total_sent_message_image_count: 0,
              total_sent_message_image_count_1day: 0,
              total_sent_message_video_count: 0,
              total_sent_message_audio_count: 0,
              total_received_message_all_count: 0,
              total_received_message_text_count: 0,
              total_received_message_image_success_count: 0,
              total_received_api_request_count: 0
            },
            createdAt: null,
            updatedAt: null,
            status: null,
            favoriteAgentIds: [],
            favoriteModelIds: [],
            favoriteExploreItemIds: [],
            favoriteToolIds: [],
            favoritePromptIds: [],
            favoriteTaskIds: [],
            favoriteTaskTemplateIds: [],
            favoriteDeckTemplateIds: [],
            pinnedConversations: [],
            activePurchase: null,
            valmar: {
              hasPaymentMethod: false,
              maskedCardData: null,
            },
            autoTopup: {
              enabled: false,
              thresholdCredits: 250,
              creditPackId: null,
              monthlySpendLimit: 50,
              currentMonthSpend: 0,
              consentedAt: null,
              lastTopupAt: null,
              lastTopupCredits: null,
              lastTopupAmount: null,
            },
            hasPassword: false,
            authProvider: 'local',
            connectedProviders: [],
            tracking: {
                completedOnboardingAt: null,
                cachedFileCount: 0,
                cachedMemoryCount: 0,
                cachedVoiceSessionCount: 0,
                cachedUniqueModelsUsedCount: 0,
                cachedImportedConversationCount: 0,
            }
          },
        });
      },

      clearSession: async () => {
        clearUsageCache();
        // Store current token for logout API call
        const currentToken = get().token;
        const { getRememberedPushToken, rememberPushToken } = require('@/lib/pushNotifications');
        const pushToken = getRememberedPushToken();

        try {
          // Hit logout while the JWT is still in the store so fetchWithAuth
          // can attach it. Also drop this device's push token so we stop
          // notifying a signed-out session.
          if (currentToken) {
            await authApi.logout({ pushToken });
          }
        } catch (error) {
          console.error('Error during logout:', error);
          // Continue with local cleanup even if API call fails
        }

        rememberPushToken(null);

        // First clear all auth state to prevent recursion
        set({
          token: null,
          refreshToken: null,
          expiresAt: null,
          isLoadingUser: false,
          hasLoadedServerUser: false,
          userError: null,
          socket: null,
          cardExpirationWarning: null
        });

        // Clear user data
        get().clearUser();

        // Remove socket listeners and disconnect
        if (getSocket().socket) {
          getSocket().socket.removeAllListeners();
          getSocket().socket.disconnect();
          getSocket().socket.close();
        }

        // Clear socket manager state and listeners
        getSocket().disconnectSocket();

        // Clear browser storage (keep appearance prefs — see webLogoutClearLocalStorage.js)
        if (typeof window !== 'undefined' && Platform.OS === 'web') {
          sessionStorage.clear();
          clearWebLocalStorageKeepingAppearancePrefs();

          // Clear all cookies
          document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
          });
        }

        // Cross-store logout fan-out lives in store/orchestration.js so this
        // leaf doesn't statically hard-couple to every sibling store
        // (Perf 3.1.b Step 3). It lazily require()s the sibling stores.
        clearAllStoresOnLogout();
      },

      addPinnedConversationId: (conversationId) => {
        const currentUser = get().user;
        const nextId = asEntityId(conversationId);
        if (!nextId) return;
        const currentPinnedIds = [...new Set((currentUser.pinnedConversations || []).map(asEntityId).filter(Boolean))];
        
        if (!currentPinnedIds.includes(nextId)) {
          set({
            user: {
              ...currentUser,
              pinnedConversations: [...currentPinnedIds, nextId]
            }
          });
        }
      },

      removePinnedConversationId: (conversationId) => {
        const currentUser = get().user;
        const nextId = asEntityId(conversationId);
        const currentPinnedIds = [...new Set((currentUser.pinnedConversations || []).map(asEntityId).filter(Boolean))];
        
        set({
          user: {
            ...currentUser,
            pinnedConversations: currentPinnedIds.filter(id => id !== nextId)
          }
        });
      },

      togglePinChat: async (conversationId) => {
        try {
          const currentUser = get().user;
          const currentPinnedIds = [...new Set((currentUser.pinnedConversations || []).map(asEntityId).filter(Boolean))];
          const isPinned = currentPinnedIds.includes(asEntityId(conversationId));
          const nextPinned = !isPinned;
          
          const success = isPinned 
            ? await getApi().chatsApi.unpinChat(conversationId)
            : await getApi().chatsApi.pinChat(conversationId);
          
          if (!success) throw 'Failed to toggle pin';

          // Update the user's pinnedConversations
          if (isPinned) {
            get().removePinnedConversationId(conversationId);
          } else {
            get().addPinnedConversationId(conversationId);
          }

          // Pass the intended next pin state. Chat-store toggle used to re-read
          // auth.pinnedConversations after it was already mutated, which
          // double-flipped isPinned and left unpinned chats sorted among pinned.
          getStores().useChatStore.getState().setChatPinned(conversationId, nextPinned);
          
          return success;
        } catch (error) {
          console.error('Error toggling pin:', error);
          throw error;
        }
      },

      // Add function to update active purchase
      setActivePurchase: (purchase) => {
        const transformedPurchase = get().transformPurchaseData(purchase);
        set(state => ({
          user: {
            ...state.user,
            activePurchase: transformedPurchase
          }
        }));
      },

      // Card expiration warning actions
      setCardExpirationWarning: (warningData) => {
        set({ cardExpirationWarning: warningData });
      },

      clearCardExpirationWarning: () => {
        set({ cardExpirationWarning: null });
      },

      // Mark that card expiration warning was shown today (stored in localStorage)
      markCardExpirationWarningShown: () => {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            window.localStorage.setItem('cardExpirationWarningLastShown', today);
          } catch (e) {
            // Silently fail if localStorage is not available
          }
        }
      },

      // Check if card expiration warning should be shown (not shown today)
      shouldShowCardExpirationWarning: () => {
        const state = get();
        if (!state.cardExpirationWarning) return false;
        
        const today = new Date().toISOString().split('T')[0];
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            const lastShown = window.localStorage.getItem('cardExpirationWarningLastShown');
            return lastShown !== today;
          } catch (e) {
            return true; // Show if can't check localStorage
          }
        }
        return true;
      },

      setDefaultModel: async (modelId, type) => {
        try {
          await getApi().accountApi.setDefaultModel(modelId, type);
          
          set(state => ({
            user: {
              ...state.user,
              defaultModel: {
                ...state.user.defaultModel,
                [type]: modelId || null
              }
            }
          }));
        } catch (error) {
          console.error('Error setting default model:', error);
          throw error;
        }
      },

      _fetchUserCreditsPromise: null,
      _fetchUserCreditsAt: 0,
      _lastUserCredits: null,

      fetchUserCredits: async ({ force = false } = {}) => {
        const state = get();

        if (state._fetchUserCreditsPromise) return state._fetchUserCreditsPromise;

        if (!force && state._fetchUserCreditsAt && Date.now() - state._fetchUserCreditsAt < 4000) {
          return state._lastUserCredits ?? null;
        }

        const promise = (async () => {
          try {
            const credits = await getApi().accountApi.getUserCredits();

            get().updateUserCredits({
              monthlyAllotmentTotal: credits.monthlyAllotment.total,
              monthlyAllotmentUsed: credits.monthlyAllotment.used,
              monthlyAllotmentRemaining: credits.monthlyAllotment.remaining,
              monthlyAllotmentResetDate: credits.monthlyAllotment.resetDate,
              purchasedCreditsAvailable: credits.purchased.available,
              paidCreditsAvailable: credits.purchased.paidAvailable,
              totalAvailableCredits: credits.totalAvailable,
              pendingVerificationCredits: credits.pendingVerificationCredits ?? 0,
            });
            set({ hasLoadedServerUser: true, _fetchUserCreditsAt: Date.now(), _lastUserCredits: credits });

            return credits;
          } catch (error) {
            console.error('Error fetching user credits:', error);
            return null;
          }
        })();

        set({ _fetchUserCreditsPromise: promise });
        promise.finally(() => set({ _fetchUserCreditsPromise: null }));
        return promise;
      },

      // Fetch paginated inference credit usage for current user
      fetchInferenceCreditUsages: async (page = 1, { startDate, endDate } = {}) => {
        try {
          // Mark loading
          set(state => ({
            user: {
              ...state.user,
              usage: {
                ...state.user.usage,
                inferenceCredits: {
                  ...state.user.usage?.inferenceCredits,
                  isLoading: true,
                  error: null,
                }
              }
            }
          }));

          const { creditUsages, pagination } = await getApi().accountApi.getUserInferenceCredits(page, { startDate, endDate });

          set(state => ({
            user: {
              ...state.user,
              usage: {
                ...state.user.usage,
                inferenceCredits: {
                  rows: page === 1
                    ? (creditUsages || [])
                    : [
                        ...(state.user?.usage?.inferenceCredits?.rows || []),
                        ...(creditUsages || [])
                      ],
                  isLoading: false,
                  error: null,
                  pagination: pagination || state.user?.usage?.inferenceCredits?.pagination
                }
              }
            }
          }));

          return { rows: creditUsages, pagination };
        } catch (error) {
          console.error('Error fetching inference credit usages:', error);
          set(state => ({
            user: {
              ...state.user,
              usage: {
                ...state.user.usage,
                inferenceCredits: {
                  ...(state.user?.usage?.inferenceCredits || {}),
                  isLoading: false,
                  error: error.message || 'Failed to fetch inference credit usages'
                }
              }
            }
          }));
          throw error;
        }
      },

      updateProfile: async (data) => {
        try {
          await getApi().accountApi.updateProfile(data);
          
          // Update local state
          set((state) => ({
            user: {
              ...state.user,
              fullName: data.fullName || state.user.fullName,
              professionalRole: data.professionalRole || state.user.professionalRole,
              avatarPath: data.removeAvatar ? null : (data.avatar ? undefined : state.user.avatarPath),
              avatarSizeBytes: data.removeAvatar ? null : (data.avatar ? undefined : state.user.avatarSizeBytes),
              tracking: {
                ...state.user.tracking,
                ...(data.previousAiTools ? { previousAiTools: data.previousAiTools } : {}),
                ...(data.interestedFeatures ? { interestedFeatures: data.interestedFeatures } : {}),
              },
            }
          }));

          return { success: true };
        } catch (error) {
          console.error('Failed to update profile:', error);
          return { success: false, error };
        }
      },

      updateUserCredits: ({
        monthlyAllotmentTotal,
        monthlyAllotmentUsed,
        monthlyAllotmentRemaining,
        monthlyAllotmentResetDate,
        purchasedCreditsAvailable,
        paidCreditsAvailable,
        totalAvailableCredits,
        pendingVerificationCredits,
      }) => {
        set(state => ({
          user: state.user ? {
            ...state.user,
            credits: {
              ...(state.user.credits || {}),
              ...(monthlyAllotmentTotal !== undefined || monthlyAllotmentUsed !== undefined || 
                 monthlyAllotmentRemaining !== undefined || monthlyAllotmentResetDate !== undefined) && {
                monthlyAllotment: {
                  ...(state.user.credits?.monthlyAllotment || {}),
                  ...(monthlyAllotmentTotal !== undefined && { total: monthlyAllotmentTotal }),
                  ...(monthlyAllotmentUsed !== undefined && { used: monthlyAllotmentUsed }),
                  ...(monthlyAllotmentRemaining !== undefined && { remaining: monthlyAllotmentRemaining }),
                  ...(monthlyAllotmentResetDate !== undefined && { resetDate: monthlyAllotmentResetDate }),
                }
              },
              ...((purchasedCreditsAvailable !== undefined || paidCreditsAvailable !== undefined) && {
                purchased: {
                  ...(state.user.credits?.purchased || {}),
                  ...(purchasedCreditsAvailable !== undefined && { available: purchasedCreditsAvailable }),
                  ...(paidCreditsAvailable !== undefined && { paidAvailable: paidCreditsAvailable }),
                }
              }),
              ...(totalAvailableCredits !== undefined && { totalAvailable: totalAvailableCredits }),
              ...(pendingVerificationCredits !== undefined && { pendingVerificationCredits }),
            }
          } : null
        }));
      },

      // MFA actions
      startMfaTotpEnrollment: async (password) => {
        const data = await getApi().mfaApi.startTotpEnrollment(password);
        return data; // { otpauthUrl, qrCodeDataUrl }
      },

      verifyMfaTotpEnrollment: async (token) => {
        const data = await getApi().mfaApi.verifyTotpEnrollment(token);
        const backupCount = Array.isArray(data?.backupCodes) ? data.backupCodes.length : 0;
        set(state => ({
          user: {
            ...state.user,
            mfa: {
              ...state.user.mfa,
              isEnabled: true,
              method: 'totp',
              hasBackupCodes: backupCount > 0,
              backupCodesRemaining: backupCount
            }
          }
        }));
        return data; // includes backupCodes
      },

      disableMfa: async (password) => {
        await getApi().mfaApi.disableMfa(password);
        set(state => ({
          user: {
            ...state.user,
            mfa: {
              isEnabled: false,
              method: null,
              hasBackupCodes: false,
              backupCodesRemaining: 0,
            }
          }
        }));
        return true;
      },
      
      trackOnboarding: async (onboardingData) => {
        try {
          const data = await getApi().usersApi.trackOnboarding(onboardingData);
          
          set(state => ({
            user: {
              ...state.user,
              tracking: {
                ...state.user.tracking,
                completedOnboardingAt: data.completedOnboardingAt
              }
            }
          }));
          
          return data;
        } catch (error) {
          console.error('Failed to track onboarding:', error);
          return null;
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => {
        // Base shape always persisted.
        const base = {
          token: state.token,
          refreshToken: state.refreshToken,
          expiresAt: state.expiresAt,
          // Persist impersonation state so the banner + Stop button survive a
          // page refresh mid-impersonation. The stashed admin token lives in
          // localStorage anyway when the admin is signed into the SPA, so this
          // adds no new secret exposure; when there's no stash it's null.
          impersonatorSession: state.impersonatorSession,
          impersonatorReturnUrl: state.impersonatorReturnUrl,
          isImpersonating: state.isImpersonating,
        };
        if (!state.user) {
          return { ...base, user: null };
        }
        // Destructure out large/transient fields to prevent localStorage quota errors.
        // Credits are intentionally server-authoritative; persisting them can flash
        // or restore stale balances over the freshly fetched value during hydration.
        const { usage, credits, ...userWithoutUsage } = state.user;
        return {
          ...base,
          user: userWithoutUsage,
        };
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState || {};
        const persistedUser = persisted.user
          ? (({ credits, ...userWithoutCredits }) => userWithoutCredits)(persisted.user)
          : persisted.user;
        const persistedPinnedIds = Array.isArray(persistedUser?.pinnedConversations)
          ? persistedUser.pinnedConversations.reduce((ids, chat) => {
            const id = chat?.id ?? chat?._id ?? chat;
            if (id) ids.push(String(id));
            return ids;
          }, [])
          : [];

        return {
          ...currentState,
          ...persisted,
          user: persistedUser
            ? {
              ...currentState.user,
              ...persistedUser,
              flags: {
                ...currentState.user?.flags,
                ...persistedUser.flags,
                // Old persisted sessions predate this flag. Fail open only
                // until the immediate server hydration returns authoritative
                // state; never leave it undefined in component selectors.
                heldForReview: persistedUser.flags?.heldForReview === true,
              },
              // Repair sessions persisted by older login flows that stored
              // pinned summary objects instead of the ids consumed by the UI.
              pinnedConversations: persistedPinnedIds,
              credits: currentState.user?.credits,
            }
            : persistedUser,
        };
      },
      onRehydrateStorage: () => () => {
        // Mark hydration as complete so components can distinguish
        // "no token" (genuinely unauthenticated) from "not yet loaded".
        //
        // IMPORTANT: deferred via queueMicrotask. On web, localStorage is
        // synchronous so this callback fires DURING create(), while
        // `useAuthStore` is still in its temporal dead zone — referencing it
        // synchronously throws inside zustand's hydration chain, which leaves
        // persist.hasHydrated() false forever. That made every web API call
        // sit through the full 2s awaitAuthHydration safety timeout.
        queueMicrotask(() => {
          useAuthStore.setState({ _hasHydrated: true });
        });
      },
    }
  )
);

export { useAuthStore };
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === 'auth-storage') syncBrowserRotation(useAuthStore.getState, useAuthStore.setState);
  });
}
export default useAuthStore;
